import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Building2, Clock, Palette, GitBranch, Save, Info, Tags, Trash2, Wifi, Database, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ensureConnected, listPrinters, isQzActive } from "../lib/qz";

const MC_MODULES = ["challans", "material_requests", "purchase_orders", "schedules", "inventory_adj"];

export default function Settings() {
  const { user } = useAuth();
  const [company, setCompany] = useState({ name: "", address: "", gst_number: "", phone: "", email: "", brand_short: "" });
  const [timing, setTiming] = useState({ start_hour: 10, end_hour: 17, override_active: false, override_reason: "" });
  const [prefs, setPrefs] = useState({ sticker_default_size: "38×38 mm (TTP-244 default)" });
  const [printer, setPrinter] = useState({ model: "TSC TTP-244 Pro", connection: "usb", ip: "", port: 9100, dpi: 203, label_width_mm: 38, label_height_mm: 38, gap_mm: 3.9, direction: 0, speed: 4, density: 8, calibration_command: "GAP 3.9 mm,0 mm" });
  const [stickerSizes, setStickerSizes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [mc, setMc] = useState({});
  const [policy, setPolicy] = useState({ user_window_hours: 48, admin_window_hours: 168 });
  const [qzPrinters, setQzPrinters] = useState([]);
  const [qzReady, setQzReady] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/company-profile"), api.get("/settings/store-timing"),
      api.get("/users/me/preferences"), api.get("/sticker-sizes"),
      api.get("/roles"), api.get("/settings/maker-checker"),
      api.get("/settings/sticker-printer"),
      api.get("/settings/deletion-policy"),
    ]).then(([c, t, p, st, r, m, sp, dp]) => {
      setCompany(c.data || {}); setTiming(t.data || {}); setPrefs(p.data || {});
      setStickerSizes(st.data || []); setRoles(r.data || []); setMc(m.data || {});
      setPrinter(sp.data || {});
      setPolicy(dp.data || { user_window_hours: 48, admin_window_hours: 168 });
    }).catch(() => {});
  }, []);

  // QZ Tray probe — runs once on mount; gives the SA quick visibility into
  // whether their workstation can print at all.
  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = await ensureConnected();
      if (!alive) return;
      setQzReady(ok);
      if (ok) setQzPrinters(await listPrinters());
    })();
    const t = setInterval(() => { if (alive) setQzReady(isQzActive()); }, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const isSA = user?.role?.is_super_admin;
  const isPH = user?.role?.name === "Plant Head/Admin";

  const saveCompany = async () => {
    try { await api.put("/company-profile", company); toast.success("Company profile saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveTiming = async () => {
    try { await api.put("/settings/store-timing", timing); toast.success("Store hours saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePrefs = async () => {
    try {
      await api.put("/users/me/preferences", prefs);
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePrinter = async () => {
    try { await api.put("/settings/sticker-printer", printer); toast.success("Printer config saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const testPrint = async () => {
    try {
      await api.post("/print/sticker/test");
      toast.success("Test sent to printer.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test print failed"); }
  };
  const savePolicy = async () => {
    try { await api.put("/settings/deletion-policy", policy); toast.success("Deletion policy saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };
  const updateMc = (mod, patch) => setMc({ ...mc, [mod]: { ...(mc[mod] || {}), ...patch } });
  const saveMc = async () => {
    try { await api.put("/settings/maker-checker", { rules: mc }); toast.success("Maker-Checker rules saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="System Settings" subtitle="Company profile, store hours, sticker printer, maker-checker"
        helpText="Super Admin can edit company profile and approval rules. Plant Head can override store outward hours and configure the sticker printer. Each user can pick their own default sticker size."/>
      <div className="p-8 grid grid-cols-2 gap-6">
        {/* Company Profile (SA only) */}
        <Section icon={Building2} title="Company Profile (Consignor)">
          {!isSA && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Super Admin only.</div>}
          <Field k="Company Name" v={company.name} d={!isSA} on={(v) => setCompany({ ...company, name: v })} t="company-name"/>
          <Field k="Address" v={company.address} d={!isSA} on={(v) => setCompany({ ...company, address: v })} multi t="company-address"/>
          <Field k="GST Number" v={company.gst_number} d={!isSA} on={(v) => setCompany({ ...company, gst_number: v })} t="company-gst"/>
          <Field k="Phone" v={company.phone} d={!isSA} on={(v) => setCompany({ ...company, phone: v })} t="company-phone"/>
          <Field k="Email" v={company.email} d={!isSA} on={(v) => setCompany({ ...company, email: v })} t="company-email"/>
          <Field k="Brand Short (PDFs/Stickers)" v={company.brand_short} d={!isSA} on={(v) => setCompany({ ...company, brand_short: v })} t="company-brand-short"/>
          {isSA && <button onClick={saveCompany} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-company-btn"><Save size={12}/> Save Company Profile</button>}
        </Section>

        {/* Store timing */}
        <Section icon={Clock} title="Store Outward Hours">
          <div className="text-xs text-[#0D2B5E]/60 mb-2">Outward Scan auto-locks outside these hours. Plant Head / SA can flip the override toggle to bypass instantly.</div>
          {!(isSA || isPH) && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Plant Head / Super Admin only.</div>}
          <Row k="Start hour (24h, IST)"><input type="number" min={0} max={23} value={timing.start_hour} disabled={!(isSA || isPH)} onChange={(e) => setTiming({ ...timing, start_hour: +e.target.value })} data-testid="timing-start" className="border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm font-mono w-24 disabled:bg-[#F5F5F5]"/></Row>
          <Row k="End hour (24h, IST)"><input type="number" min={1} max={24} value={timing.end_hour} disabled={!(isSA || isPH)} onChange={(e) => setTiming({ ...timing, end_hour: +e.target.value })} data-testid="timing-end" className="border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm font-mono w-24 disabled:bg-[#F5F5F5]"/></Row>
          <Row k="Override Active">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!timing.override_active} disabled={!(isSA || isPH)} onChange={(e) => setTiming({ ...timing, override_active: e.target.checked })} data-testid="timing-override"/>
              <span className="text-xs uppercase tracking-widest font-bold" style={{ color: timing.override_active ? "#E65100" : "#2E7D32" }}>{timing.override_active ? "ACTIVE — outward open 24h" : "OFF — window enforced"}</span>
            </label>
          </Row>
          {timing.override_active && <Field k="Override Reason" v={timing.override_reason} d={!(isSA || isPH)} on={(v) => setTiming({ ...timing, override_reason: v })} t="timing-reason"/>}
          {(isSA || isPH) && <button onClick={saveTiming} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-timing-btn"><Save size={12}/> Save Store Hours</button>}
        </Section>

        {/* User prefs (sticker default) */}
        <Section icon={Palette} title="My Preferences">
          <Row k="Default Sticker Size">
            <select value={prefs.sticker_default_size || ""} onChange={(e) => setPrefs({ ...prefs, sticker_default_size: e.target.value })} data-testid="pref-sticker-size" className="border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm">
              {stickerSizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Row>
          <button onClick={savePrefs} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-prefs-btn"><Save size={12}/> Save Preferences</button>
        </Section>

        {/* Sticker Printer Config (TTP-244 Pro) */}
        <Section icon={Tags} title="Sticker Printer (TSC TTP-244 Pro · 203 DPI)">
          {!(isSA || isPH) && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Plant Head / Super Admin only.</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field k="Model" v={printer.model} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, model: v })} t="printer-model"/>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Connection</label>
              <select value={printer.connection} disabled={!(isSA || isPH)} onChange={(e) => setPrinter({ ...printer, connection: e.target.value })} data-testid="printer-connection" className="w-full border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm disabled:bg-[#F5F5F5]">
                <option value="usb">USB</option><option value="network">Network (TCP)</option><option value="bluetooth">Bluetooth</option>
              </select>
            </div>
            {printer.connection === "network" && <>
              <Field k="IP Address" v={printer.ip} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, ip: v })} t="printer-ip"/>
              <Field k="Port" v={printer.port} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, port: +v || 9100 })} t="printer-port"/>
            </>}
            <Field k="DPI" v={printer.dpi} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, dpi: +v || 203 })} t="printer-dpi"/>
            <Field k="Label Width (mm)" v={printer.label_width_mm} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, label_width_mm: +v })} t="printer-w"/>
            <Field k="Label Height (mm)" v={printer.label_height_mm} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, label_height_mm: +v })} t="printer-h"/>
            <Field k="Gap (mm)" v={printer.gap_mm} d={!(isSA || isPH)} on={(v) => {
              const g = Math.max(0, Math.round((+v || 0) * 10) / 10);  // one decimal place
              setPrinter({ ...printer, gap_mm: g, calibration_command: `GAP ${g} mm,0 mm` });
            }} t="printer-gap" step="0.1"/>
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Direction</label>
              <select value={printer.direction ?? 0} disabled={!(isSA || isPH)} onChange={(e) => setPrinter({ ...printer, direction: +e.target.value })} data-testid="printer-direction" className="w-full border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm disabled:bg-[#F5F5F5]">
                <option value={0}>0 — Normal</option>
                <option value={1}>1 — Inverted</option>
              </select>
              <div className="text-[10px] text-[#0D2B5E]/50 mt-1">Switch to 1 if stickers print upside-down.</div>
            </div>
            <Field k="Speed (ips, 1–6)" v={printer.speed} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, speed: +v })} t="printer-speed"/>
            <Field k="Density (0–15)" v={printer.density} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, density: +v })} t="printer-density"/>
            <div className="col-span-2">
              <Field k="Calibration Command (TSPL)" v={printer.calibration_command} d={!(isSA || isPH)} on={(v) => setPrinter({ ...printer, calibration_command: v })} t="printer-calib"/>
            </div>
          </div>
          {(isSA || isPH) && <button onClick={savePrinter} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-printer-btn"><Save size={12}/> Save Printer Config</button>}
          {(isSA || isPH) && printer.connection === "network" && (
            <button onClick={testPrint} className="mt-3 ml-2 bg-[#2E7D32] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-2" data-testid="test-print-btn"><Wifi size={12}/> Test Print (network)</button>
          )}
          <div className="mt-3 p-2 rounded-sm border text-xs flex items-start gap-2"
               style={{ borderColor: qzReady ? "#2E7D3260" : "#C6282860", background: qzReady ? "#2E7D320A" : "#C628280A", color: qzReady ? "#2E7D32" : "#C62828" }}
               data-testid="qz-tray-status">
            <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: qzReady ? "#2E7D32" : "#C62828" }}/>
            <div className="flex-1">
              <div className="font-bold">QZ Tray (USB bridge): {qzReady ? "Connected" : "Not running"}</div>
              {qzReady ? (
                <div className="text-[10px] mt-0.5 break-all">Available printers: {qzPrinters.length ? qzPrinters.join(" · ") : "none detected"}</div>
              ) : (
                <div className="text-[10px] mt-0.5">Install / start <strong>QZ Tray</strong> on each Store workstation that has the TTP-244 plugged in via USB. Free download: <a className="underline" href="https://qz.io/download/" target="_blank" rel="noreferrer">qz.io/download</a></div>
              )}
            </div>
          </div>
          <div className="text-[10px] text-[#0D2B5E]/50 mt-2">Standard label sizes: 38×38mm · 35×30mm · 40×30mm. Driver: TSPL2.</div>
        </Section>

        {/* Deletion Policy (SA only) */}
        <Section icon={Trash2} title="Deletion Policy">
          {!isSA && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Super Admin only.</div>}
          <div className="text-xs text-[#0D2B5E]/70 leading-relaxed mb-3">
            Operators can request deletion of records they created within the User Window. Plant Head / Admin can request deletion of any record within the Admin Window. Super Admin can delete instantly with reason. Approved deletions are SOFT — restorable from Trash.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field k="User Window (hours)" v={policy.user_window_hours} d={!isSA} on={(v) => setPolicy({ ...policy, user_window_hours: +v || 48 })} t="policy-user-window"/>
            <Field k="Admin Window (hours)" v={policy.admin_window_hours} d={!isSA} on={(v) => setPolicy({ ...policy, admin_window_hours: +v || 168 })} t="policy-admin-window"/>
          </div>
          {isSA && <button onClick={savePolicy} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-policy-btn"><Save size={12}/> Save Deletion Policy</button>}
        </Section>

        {/* Maker-checker */}
        <Section icon={GitBranch} title="Maker-Checker Rules (Extensible)">
          {!isSA && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Super Admin only — read-only view.</div>}
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr><th className="text-left">Module</th><th>Enabled</th><th>Maker</th><th>Checker</th></tr>
            </thead>
            <tbody>
              {MC_MODULES.map((mod) => (
                <tr key={mod} className="border-t border-[#EEEEEE]">
                  <td className="py-2 capitalize">{mod.replace("_", " ")}</td>
                  <td className="text-center"><input type="checkbox" disabled={!isSA} checked={!!mc[mod]?.enabled} onChange={(e) => updateMc(mod, { enabled: e.target.checked })} data-testid={`mc-${mod}-enabled`}/></td>
                  <td className="text-center">
                    <select disabled={!isSA} value={mc[mod]?.maker_role || ""} onChange={(e) => updateMc(mod, { maker_role: e.target.value })} className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm" data-testid={`mc-${mod}-maker`}>
                      <option value="">—</option>{roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </td>
                  <td className="text-center">
                    <select disabled={!isSA} value={mc[mod]?.checker_role || ""} onChange={(e) => updateMc(mod, { checker_role: e.target.value })} className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm" data-testid={`mc-${mod}-checker`}>
                      <option value="">—</option>{roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isSA && <button onClick={saveMc} className="mt-3 bg-[#1565C0] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="save-mc-btn"><Save size={12}/> Save Rules</button>}
        </Section>

        {/* Backups & Exports (SA + PH only) */}
        <BackupsSection isSA={isSA} isPH={isPH}/>

        {/* Info */}
        <Section icon={Tags} title="Sequence & Branding Notes">
          <div className="text-xs text-[#0D2B5E]/70 leading-relaxed">
            <Info size={12} className="inline mr-1"/>Sequences: GR / GRN / LOT / ISS / PO / DC / MR / ADJ / SCH — annual reset on new financial year.
            <br/><br/>Brand short ({company.brand_short || "SEPL"}) is rendered in stickers, inspection PDFs and Delivery Challan headers.
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm">
      <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center gap-2 bg-[#F5F5F5]">
        <Icon size={14} className="text-[#1565C0]"/>
        <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">{title}</div>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}
function Row({ k, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">{k}</span>
      {children}
    </div>
  );
}
function Field({ k, v, d, on, multi, t, step }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">{k}</label>
      {multi ? (
        <textarea rows={2} value={v || ""} disabled={d} onChange={(e) => on(e.target.value)} data-testid={t} className="w-full border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm disabled:bg-[#F5F5F5]"/>
      ) : (
        <input
          type={step ? "number" : "text"}
          step={step}
          value={v ?? ""}
          disabled={d}
          onChange={(e) => on(e.target.value)}
          data-testid={t}
          className="w-full border border-[#EEEEEE] px-3 py-1.5 text-sm rounded-sm disabled:bg-[#F5F5F5]"/>
      )}
    </div>
  );
}

function BackupsSection({ isSA, isPH }) {
  const [meta, setMeta] = useState({ backups: [], scheduled_hour_utc: 20, retention_days: 30 });
  const [collections, setCollections] = useState([]);
  const [busy, setBusy] = useState(false);
  const canView = isSA || isPH;

  const loadAll = async () => {
    if (!canView) return;
    try {
      const [b, c] = await Promise.all([
        api.get("/backups"),
        api.get("/exports/collections"),
      ]);
      setMeta(b.data); setCollections(c.data.collections || []);
    } catch (e) {
      // Endpoint may be unavailable while a freshly-deployed instance warms
      // up; log so failures are visible during local development but stay
      // silent for the user (Settings page degrades gracefully without
      // backups/exports metadata).
      console.warn("[Settings.loadAll] failed:", e?.response?.status, e?.message);
    }
  };
  useEffect(() => { loadAll(); }, [canView]);  // eslint-disable-line

  const triggerBackup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backups/now");
      toast.success(r.data.skipped ? `Today's backup already exists (${fmtBytes(r.data.size)})` : `Backup created: ${r.data.filename} · ${fmtBytes(r.data.size)}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup failed");
    }
    setBusy(false);
  };

  const downloadBackup = async (filename) => {
    try {
      const r = await api.get(`/backups/${filename}`, { responseType: "blob" });
      saveBlob(r.data, filename);
    } catch (e) { toast.error("Download failed"); }
  };

  const downloadJsonAll = async () => {
    try {
      const r = await api.get("/exports/json", { responseType: "blob" });
      saveBlob(r.data, `supplar-export-${new Date().toISOString().slice(0,10)}.json`);
      toast.success("JSON archive downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };

  const downloadCsv = async (collection) => {
    try {
      const r = await api.get(`/exports/csv/${collection}`, { responseType: "blob" });
      saveBlob(r.data, `${collection}-${new Date().toISOString().slice(0,10)}.csv`);
    } catch (e) { toast.error(`CSV for ${collection} failed`); }
  };

  if (!canView) {
    return (
      <Section icon={Database} title="Backups & Exports">
        <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm">Super Admin / Plant Head only.</div>
      </Section>
    );
  }

  return (
    <Section icon={Database} title="Backups & Exports">
      <div className="text-xs text-[#0D2B5E]/70 leading-relaxed mb-3">
        <Info size={12} className="inline mr-1"/>
        Automatic <strong>nightly backup</strong> at {String(meta.scheduled_hour_utc).padStart(2,"0")}:00 UTC →
        <code className="bg-[#F5F5F5] px-1 mx-1">/app/backend/backups/YYYY-MM-DD.gz</code>
        (retention {meta.retention_days} days). Download manually any time, or run on-demand exports below.
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {isSA && (
          <button onClick={triggerBackup} disabled={busy} className="bg-[#1565C0] hover:bg-[#0D2B5E] disabled:bg-[#BBB] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="backup-now-btn">
            <Database size={12}/> {busy ? "Running…" : "Run Backup Now"}
          </button>
        )}
        <button onClick={downloadJsonAll} className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs px-4 py-2 rounded-sm uppercase tracking-wider font-bold flex items-center gap-2" data-testid="export-json-all-btn">
          <Download size={12}/> Download Full JSON
        </button>
      </div>

      {/* Backup files list */}
      <div className="mb-4">
        <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Available Backups ({meta.backups.length})</div>
        {meta.backups.length === 0 ? (
          <div className="text-xs text-[#0D2B5E]/50 italic">No backups yet — click "Run Backup Now" to create the first one.</div>
        ) : (
          <div className="border border-[#EEEEEE] rounded-sm max-h-32 overflow-auto" data-testid="backups-list">
            {meta.backups.map((b) => (
              <div key={b.filename} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-[#EEEEEE] last:border-0 text-xs">
                <span className="font-mono text-[#0D2B5E]">{b.filename}</span>
                <span className="text-[#0D2B5E]/60">{fmtBytes(b.size)}</span>
                <button onClick={() => downloadBackup(b.filename)} className="text-[#1565C0] hover:text-[#0D2B5E] underline" data-testid={`backup-download-${b.filename}`}>Download</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSV exports per collection */}
      <div>
        <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">CSV Exports by Collection</div>
        <div className="grid grid-cols-3 gap-1 max-h-40 overflow-auto" data-testid="csv-export-list">
          {collections.map((c) => (
            <button key={c.name} onClick={() => downloadCsv(c.name)} disabled={c.count <= 0} className="text-left text-[11px] px-2 py-1 border border-[#EEEEEE] rounded-sm hover:border-[#1565C0] hover:text-[#1565C0] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-1" data-testid={`csv-${c.name}`}>
              <span className="truncate">{c.name}</span>
              <span className="text-[#0D2B5E]/50 font-mono">{c.count}</span>
            </button>
          ))}
        </div>
      </div>
    </Section>
  );
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
